import { useState } from "react";
import {
  ArrowLeft,
  Upload,
  Loader2,
  FileCheck,
  Columns,
  AlertTriangle,
  Info,
  MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/lib/trpc";
import { DEFAULT_CLASSIFICATION_TYPES } from "@/types/domain";
import { useImportProgress } from "@/hooks/useImportProgress";
import type { WizardState, WizardAction } from "../ImportPage";

interface Props {
  state: WizardState;
  dispatch: React.Dispatch<WizardAction>;
}

const uniqueSportValues = DEFAULT_CLASSIFICATION_TYPES.map((t) => ({
  label: t.replace(/_/g, " "),
  value: t,
}));

export function PreviewStep({ state, dispatch }: Props) {
  const [runMatching, setRunMatching] = useState(true);
  const [importId, setImportId] = useState<string | null>(null);
  const { data: prodStatus } = trpc.import.isProdConfigured.useQuery();
  const progress = useImportProgress(importId);

  const executeMutation = trpc.import.execute.useMutation({
    onSuccess: (result) => {
      dispatch({ type: "IMPORT_COMPLETE", result });
    },
    onError: (err) => {
      dispatch({ type: "SET_ERROR", error: err.message });
    },
  });

  const {
    parseResult,
    columnMappings,
    sportTypeMappings,
    userSportOverrides,
    validationIssues,
  } = state;

  if (!parseResult) return null;

  const mappedColumns = columnMappings.filter(
    (m) => m.targetColumn !== null,
  ).length;
  const skippedColumns = columnMappings.filter(
    (m) => m.targetColumn === null,
  ).length;
  const blockingErrors = validationIssues.filter(
    (i) => i.severity === "error",
  );
  const hasBlockingErrors = blockingErrors.length > 0;

  const handleImport = () => {
    if (!state.fileData || !state.fileName || !state.fileType) return;

    const id = state.needsGeocoding ? crypto.randomUUID() : undefined;
    if (id) setImportId(id);

    dispatch({ type: "SET_LOADING", isLoading: true });

    executeMutation.mutate({
      fileData: state.fileData,
      fileName: state.fileName,
      fileType: state.fileType,
      selectedSheet: parseResult.selectedSheet,
      columnMappings,
      coordinateFormat: parseResult.coordinateFormat,
      coordinateSourceColumns: parseResult.coordinateSourceColumns,
      sportTypeOverrides: userSportOverrides,
      runMatching,
      source: state.sourceType,
      addressConfig: state.addressConfig,
      importId: id,
    });
  };

  const isLoading = state.isLoading || executeMutation.isPending;
  const isGeocoding =
    isLoading && progress.total > 0 && progress.current < progress.total;

  return (
    <div className="space-y-6">
      {/* Summary cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <FileCheck className="w-5 h-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">{parseResult.totalRows}</p>
              <p className="text-xs text-muted-foreground">Total Rows</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Columns className="w-5 h-5 text-primary" />
            <div>
              <p className="text-2xl font-semibold">{mappedColumns}</p>
              <p className="text-xs text-muted-foreground">
                Mapped Columns
                {skippedColumns > 0 && (
                  <span className="text-yellow-600 dark:text-yellow-400">
                    {" "}({skippedColumns} skipped)
                  </span>
                )}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle
              className={`w-5 h-5 ${blockingErrors.length > 0 ? "text-destructive" : "text-green-600"}`}
            />
            <div>
              <p className="text-2xl font-semibold">
                {validationIssues.length}
              </p>
              <p className="text-xs text-muted-foreground">
                {blockingErrors.length > 0 ? "Issues Found" : "Issues"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Geocoding info */}
      {state.needsGeocoding && state.addressConfig && (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 rounded-lg border border-blue-500/30 bg-blue-500/10 p-4">
          <MapPin className="w-4 h-4 shrink-0" />
          <span>
            Addresses will be geocoded via Google Geocoding API during import.
          </span>
        </div>
      )}

      {/* Blocking errors */}
      {hasBlockingErrors && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 space-y-1">
          <p className="text-sm font-medium text-destructive">
            Blocking errors — cannot import:
          </p>
          {blockingErrors.map((issue, i) => (
            <p key={i} className="text-sm text-destructive">
              {issue.message}
            </p>
          ))}
        </div>
      )}

      {/* Sport type review */}
      {sportTypeMappings.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sport Type Mapping</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left p-3 font-medium">
                      Original Value
                    </th>
                    <th className="text-left p-3 font-medium">
                      Mapped To
                    </th>
                    <th className="text-left p-3 font-medium w-[220px]">
                      Override
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sportTypeMappings.map((mapping) => {
                    const overridden =
                      userSportOverrides[mapping.originalValue] !== undefined;
                    const displayValue = overridden
                      ? userSportOverrides[mapping.originalValue]
                      : mapping.mappedValue;

                    return (
                      <tr
                        key={mapping.originalValue}
                        className="border-b last:border-b-0"
                      >
                        <td className="p-3 font-mono text-xs">
                          {mapping.originalValue}
                        </td>
                        <td className="p-3">
                          {displayValue ? (
                            <Badge variant="secondary" className="text-xs">
                              {displayValue}
                              {mapping.isFallback && !overridden && (
                                <span className="text-yellow-600 dark:text-yellow-400 ml-1">
                                  (fallback)
                                </span>
                              )}
                            </Badge>
                          ) : (
                            <Badge
                              variant="outline"
                              className="text-xs text-muted-foreground"
                            >
                              (none)
                            </Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <Select
                            value={
                              userSportOverrides[
                                mapping.originalValue
                              ] ?? "_default"
                            }
                            onValueChange={(val) => {
                              if (val === "_default") {
                                const next = {
                                  ...userSportOverrides,
                                };
                                delete next[mapping.originalValue];
                                dispatch({
                                  type: "UPDATE_SPORT_OVERRIDE",
                                  original: mapping.originalValue,
                                  mapped: val === "_default" ? mapping.mappedValue ?? "" : val,
                                });
                                return;
                              }
                              dispatch({
                                type: "UPDATE_SPORT_OVERRIDE",
                                original: mapping.originalValue,
                                mapped: val === "_none" ? "" : val,
                              });
                            }}
                          >
                            <SelectTrigger className="h-8 text-xs w-full">
                              <SelectValue placeholder="Default" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_default">
                                Default mapping
                              </SelectItem>
                              <SelectItem value="_none">
                                (skip — no mapping)
                              </SelectItem>
                              {uniqueSportValues.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label} → {opt.value}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Options */}
      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-3">
            <Checkbox
              id="run-matching"
              checked={runMatching}
              onCheckedChange={(checked) =>
                setRunMatching(checked === true)
              }
            />
            <label htmlFor="run-matching" className="text-sm cursor-pointer">
              <span className="font-medium">Run location matching</span>
              <span className="text-muted-foreground ml-1">
                — match imported locations with scanned locations (100m
                threshold)
              </span>
            </label>
          </div>

          {prodStatus?.configured && (
            <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
              <Info className="w-4 h-4" />
              <span>
                Production Supabase is configured — data will be written to
                both local and production databases.
              </span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Geocoding progress */}
      {isGeocoding && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              Geocoding {progress.current}/{progress.total} addresses...
            </div>
            <Progress
              value={(progress.current / progress.total) * 100}
              className="h-2"
            />
            {progress.lastAddress && (
              <p className="text-xs text-muted-foreground truncate">
                Last: {progress.lastAddress}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Loading state (non-geocoding) */}
      {isLoading && !isGeocoding && progress.current >= progress.total && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              Importing locations...
            </div>
          </CardContent>
        </Card>
      )}

      {/* Error */}
      {state.error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-sm text-destructive">
          {state.error}
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between">
        <Button
          variant="outline"
          onClick={() =>
            dispatch({
              type: "GOTO_STEP",
              step: state.needsGeocoding ? "address" : "mapping",
            })
          }
          disabled={isLoading}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        <Button
          onClick={handleImport}
          disabled={hasBlockingErrors || isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Importing...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              Import {parseResult.totalRows} Rows
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
